import { Injectable, inject } from '@angular/core';
import { AuthTokenStorageService } from '@zerops/zef/auth';

const userStorageKey = '@zerops/zerops/user-data';

interface UserDataPayload {
  userId: string;
  clientUserId: string;
}

@Injectable({ providedIn: 'root' })
export class UserDataStorageService {
  private _data: UserDataPayload;
  private _tokenStorage = inject(AuthTokenStorageService);

  setData(data: UserDataPayload) {
    this._data = data;
    this._updateStorage();
    // Track clientUserId per-tab so boot flow can detect cross-tab org changes
    if (data?.clientUserId) {
      this._tokenStorage.setSessionClientUserId(data.clientUserId);
    }
  }

  removeData() {
    this._data = undefined;
    this._updateStorage();
  }

  getData() {
    return this._data;
  }

  constructor() {
    this._data = this._getStorage();

    window.addEventListener('storage', (event) => {
      if (event.key !== userStorageKey) {
        return;
      }

      // Already invalidated — ignore further changes
      if (this._tokenStorage.sessionInvalidated()) {
        return;
      }

      // User data cleared (logout handles this separately via token removal)
      if (!event.newValue) {
        return;
      }

      try {
        const parsed: UserDataPayload = JSON.parse(event.newValue);

        // Different organization selected in another tab
        if (this._data && parsed.clientUserId && this._data.clientUserId !== parsed.clientUserId) {
          this._tokenStorage.sessionInvalidated.set(true);
          this._tokenStorage.sessionInvalidReason.set('clientChanged');
          this._tokenStorage.crossTabSessionInvalidated$.next();
          return;
        }

        // Same org — silent update
        this._data = parsed;
      } catch {
        // Ignore malformed data
      }
    });
  }

  private _updateStorage() {
    const d = this._data
      ? JSON.stringify(this._data)
      : '';

    localStorage.setItem(userStorageKey, d);
  }

  private _getStorage() {
    const s = localStorage.getItem(userStorageKey);

    return s ? JSON.parse(s) : undefined;
  }
}
